export type ChatTimestampValue = Date | string | number | null | undefined;

const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const RELATIVE_DATE_LIMIT_DAYS = 7;

/**
 * The chat API currently returns UTC DateTime values without a timezone suffix,
 * for example: 2026-07-17T06:23:00.637092
 *
 * JavaScript otherwise interprets that value as browser-local time. Normalize
 * timezone-less API values to UTC and trim sub-millisecond precision so every
 * browser parses the same instant.
 */
export const normalizeChatDateString = (value: string): string => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return trimmedValue;
  }

  const hasUtcSuffix = /Z$/i.test(trimmedValue);
  const hasNumericOffset = /[+-]\d{2}:?\d{2}$/.test(trimmedValue);

  if (hasUtcSuffix || hasNumericOffset) {
    return trimmedValue;
  }

  // JavaScript dates use millisecond precision. Convert .637092 to .637.
  const millisecondNormalized = trimmedValue.replace(
    /\.(\d{3})\d+$/,
    ".$1",
  );

  return `${millisecondNormalized}Z`;
};

export const toChatTimestamp = (value: ChatTimestampValue): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const timestamp = Date.parse(normalizeChatDateString(value));
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const formatChatTimestamp = (
  value: ChatTimestampValue,
  nowValue: Date | number = Date.now(),
): string => {
  const timestamp = toChatTimestamp(value);
  const nowTimestamp =
    nowValue instanceof Date ? nowValue.getTime() : nowValue;

  if (timestamp === null || !Number.isFinite(nowTimestamp)) {
    return "";
  }

  const differenceMs = nowTimestamp - timestamp;

  // A small future difference can happen from clock skew or network timing.
  // A large future difference should no longer happen once UTC normalization
  // is applied, but still avoid displaying a misleading negative duration.
  if (differenceMs <= 0) {
    return "just now";
  }

  const differenceSeconds = Math.floor(differenceMs / SECOND);
  const differenceMinutes = Math.floor(differenceMs / MINUTE);
  const differenceHours = Math.floor(differenceMs / HOUR);
  const differenceDays = Math.floor(differenceMs / DAY);

  if (differenceSeconds < 10) {
    return "just now";
  }

  if (differenceSeconds < 60) {
    return `${differenceSeconds} seconds ago`;
  }

  if (differenceMinutes < 60) {
    return `${differenceMinutes} minute${differenceMinutes === 1 ? "" : "s"} ago`;
  }

  if (differenceHours < 24) {
    return `${differenceHours} hour${differenceHours === 1 ? "" : "s"} ago`;
  }

  if (differenceDays < RELATIVE_DATE_LIMIT_DAYS) {
    return `${differenceDays} day${differenceDays === 1 ? "" : "s"} ago`;
  }

  const date = new Date(timestamp);
  const now = new Date(nowTimestamp);
  const includeYear = date.getFullYear() !== now.getFullYear();

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: includeYear ? "numeric" : undefined,
  });
};